import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { io } from 'socket.io-client';
import { productName, productSubtitle } from '@bramx/ui';
import { SquadPanel } from './components/SquadPanel';
import { LeagueBoard } from './components/LeagueBoard';
import { DecisionLab } from './components/DecisionLab';
import { DecisionIntelligence } from './components/DecisionIntelligence';
import {
  compareEntries,
  captureLearningOutcomes,
  getBootstrap,
  getCurrentGameweek,
  getFeedHealth,
  getFixtures,
  getLeagueStandings,
  getLatestPlannedSquad,
  getMatchCentre,
  getPublicConfig,
  replaySimulation,
  hasAdminToken,
  type Fixture,
  type LeagueStanding,
  type ProviderFixtureBundle,
  type BramxCompareResponse,
  type FeedHealthResponse,
  type PlannedSquadPayload,
} from './lib/api';
import { useDuelStore } from './store/useDuelStore';
import { currentGameweekRefreshPolicy, FPL_UPDATE_INVALIDATION_ROOTS } from './lib/gameweek-refresh';
import { adjacentGameweek, resolveSelectedGameweekContext, syncSelectedGameweek, type GameweekMode } from './lib/gameweek-context';
import { deriveDataHealthStatus } from './lib/data-health';
import { connectionPills, feedTone, groupFeeds, providerProvenance } from './lib/feed-provenance';
import { compactDuelSummary } from './lib/duel-summary';
import { groupFplFixtures, groupProviderFixtures, type MatchGroup } from './lib/match-centre';
import { buildTeamMatchDisplayStates } from './lib/live-player-state';
import { chooseInitialPlannedSquad } from './lib/planned-squad-hydration';

type RadarMode = 'MINI' | 'OVERALL';

export default function App() {
  const store = useDuelStore();
  const queryClient = useQueryClient();
  const [activeDuel, setActiveDuel] = useState<{ entryA: string; entryB: string } | null>(null);
  const [activeMiniLeagueId, setActiveMiniLeagueId] = useState<number | null>(null);
  const [radarMode, setRadarMode] = useState<RadarMode>('MINI');
  const [selectedFixtureId, setSelectedFixtureId] = useState<string | null>(null);
  const [selectedGameweek, setSelectedGameweek] = useState<number | null>(null);
  const [playersOpen, setPlayersOpen] = useState(false);
  const [socketStatus, setSocketStatus] = useState('CONNECTING');
  const [socketTransport, setSocketTransport] = useState<string | null>(null);
  const defaultsLoaded = useRef(false);
  const previousCurrentGw = useRef<number | null>(null);
  const leagueBoardRef = useRef<HTMLDivElement | null>(null);
  const plannedHydrated = useRef(new Set<string>());
  const [duelRunning, setDuelRunning] = useState(false);
  const [radarLoading, setRadarLoading] = useState<RadarMode | null>(null);
  const [controlMessage, setControlMessage] = useState('READY · één Gameweek Context bepaalt LIVE, PREVIEW of REPLAY.');
  const [clockMs, setClockMs] = useState(() => Date.now());
  const [adminUnlocked, setAdminUnlocked] = useState(() => hasAdminToken());
  const [analysisPlan, setAnalysisPlan] = useState<PlannedSquadPayload | null>(null);

  const configQuery = useQuery({ queryKey: ['public-config'], queryFn: getPublicConfig, staleTime: Infinity });
  const gameweekQuery = useQuery({
    queryKey: ['current-gw'],
    queryFn: getCurrentGameweek,
    staleTime: 10_000,
    refetchInterval: (query) => currentGameweekRefreshPolicy(query.state.data, Date.now()).refetchIntervalMs,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });
  const bootstrapQuery = useQuery({ queryKey: ['bootstrap'], queryFn: getBootstrap, staleTime: 5 * 60_000, refetchInterval: 5 * 60_000, refetchOnWindowFocus: true });
  const gw = gameweekQuery.data?.gameweek ?? null;
  const context = resolveSelectedGameweekContext(gameweekQuery.data, selectedGameweek);
  const targetGw = context.selectedGameweek;
  const mode = context.mode;
  const apiScope = context.apiScope;
  const plannedForGw = adminUnlocked && mode === 'PREVIEW' && targetGw && analysisPlan?.gameweek === targetGw ? analysisPlan : null;
  const overallLeagueId = configQuery.data?.overallLeagueId ?? 314;

  useEffect(() => {
    if (gw === null) return;
    setSelectedGameweek((current) => syncSelectedGameweek(current, previousCurrentGw.current, gw));
    previousCurrentGw.current = gw;
  }, [gw]);

  const serverPlanQuery = useQuery({
    queryKey: ['planned-squad-server', activeDuel?.entryA, targetGw],
    queryFn: () => getLatestPlannedSquad(activeDuel!.entryA, targetGw!),
    enabled: Boolean(adminUnlocked && mode === 'PREVIEW' && activeDuel?.entryA && targetGw),
    staleTime: Infinity,
    retry: false,
  });
  const fplFixturesQuery = useQuery({
    queryKey: ['fixtures', targetGw],
    queryFn: () => getFixtures(targetGw!),
    enabled: typeof targetGw === 'number',
    refetchInterval: mode === 'LIVE' ? 60_000 : false,
  });
  const healthQuery = useQuery({ queryKey: ['feed-health'], queryFn: getFeedHealth, refetchInterval: 15_000 });
  const miniLeagueQuery = useQuery({
    queryKey: ['mini-league-top100', activeMiniLeagueId],
    queryFn: () => getLeagueStandings(activeMiniLeagueId!, 100),
    enabled: mode !== 'REPLAY' && activeMiniLeagueId !== null,
    refetchInterval: 60_000,
  });
  const overallLeagueQuery = useQuery({
    queryKey: ['overall-top100', overallLeagueId],
    queryFn: () => getLeagueStandings(overallLeagueId, 100),
    enabled: mode !== 'REPLAY' && radarMode === 'OVERALL',
    refetchInterval: 60_000,
  });
  const compareQuery = useQuery({
    queryKey: ['bramx-compare', activeDuel?.entryA, activeDuel?.entryB, targetGw, apiScope, plannedForGw],
    queryFn: () => compareEntries(activeDuel!.entryA, activeDuel!.entryB, targetGw!, apiScope!, plannedForGw),
    enabled: Boolean(activeDuel && targetGw && apiScope),
    refetchInterval: mode === 'LIVE' ? 30_000 : false,
  });
  const matchCentreQuery = useQuery({
    queryKey: ['match-centre'],
    queryFn: getMatchCentre,
    enabled: mode === 'LIVE',
    staleTime: 0,
    refetchInterval: 10_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 1,
  });

  useEffect(() => {
    if (mode !== 'PREVIEW' || !activeDuel?.entryA || !targetGw || !serverPlanQuery.data) return;
    const key = `${activeDuel.entryA}:${targetGw}`;
    if (plannedHydrated.current.has(key)) return;
    plannedHydrated.current.add(key);
    const hydrated = chooseInitialPlannedSquad(store.plannedA, serverPlanQuery.data.plan, targetGw);
    if (hydrated.plan) {
      // First hydrate for an entry/GW: the server-saved private plan is authoritative.
      // This deliberately replaces a stale same-GW browser draft (for example one
      // with null selling prices left behind by an older build). Later local edits
      // remain untouched because plannedHydrated gates this block to one execution.
      store.setPlannedA(hydrated.plan);
      setAnalysisPlan(hydrated.plan);
    }
  }, [mode, activeDuel?.entryA, targetGw, serverPlanQuery.data, store]);

  useEffect(() => {
    const onAdminTokenChanged = () => {
      // Re-unlock must be a real private-state reload, not just an auth toggle.
      // Clear the one-shot hydration gate so a fresh server plan can replace a
      // stale same-GW browser draft (for example FT=1 / blank cash / null sells).
      plannedHydrated.current.clear();
      setAdminUnlocked(hasAdminToken());
      void queryClient.invalidateQueries({ queryKey: ['planned-squad-server'], refetchType: 'active' });
    };
    window.addEventListener('bramx-admin-token-changed', onAdminTokenChanged);
    return () => window.removeEventListener('bramx-admin-token-changed', onAdminTokenChanged);
  }, [queryClient]);

  useEffect(() => {
    const timer = window.setInterval(() => setClockMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (defaultsLoaded.current || !configQuery.data || !targetGw) return;
    defaultsLoaded.current = true;
    const bram = store.entryA || (configQuery.data.defaultEntries.bram ? String(configQuery.data.defaultEntries.bram) : '');
    const viktor = store.entryB || (configQuery.data.defaultEntries.viktor ? String(configQuery.data.defaultEntries.viktor) : '');
    const storedLeague = parseLeagueId(store.leagueId);
    const miniLeague = !storedLeague || storedLeague === configQuery.data.overallLeagueId ? String(configQuery.data.defaultLeagueId) : store.leagueId;
    if (!store.entryA && bram) store.setEntryA(bram);
    if (!store.entryB && viktor) store.setEntryB(viktor);
    if ((!store.leagueId || storedLeague === configQuery.data.overallLeagueId) && miniLeague) store.setLeagueId(miniLeague);
    if (/^\d+$/.test(bram) && /^\d+$/.test(viktor)) setActiveDuel({ entryA: bram, entryB: viktor });
    const parsedMini = parseLeagueId(miniLeague);
    if (parsedMini) setActiveMiniLeagueId(parsedMini);
  }, [configQuery.data, targetGw, store]);

  useEffect(() => {
    setSelectedFixtureId(null);
    setPlayersOpen(false);
  }, [targetGw, mode]);

  useEffect(() => {
    const socket = io({
      path: '/socket.io',
      transports: ['polling', 'websocket'],
      upgrade: true,
      timeout: 10_000,
      reconnection: true,
      reconnectionAttempts: Infinity,
    });
    const updateTransport = () => setSocketTransport(socket.io.engine?.transport?.name ?? null);
    socket.on('connect', () => {
      setSocketStatus('OK');
      updateTransport();
      socket.io.engine?.on('upgrade', updateTransport);
    });
    socket.on('disconnect', () => {
      setSocketStatus('DOWN');
      setSocketTransport(null);
    });
    socket.on('connect_error', () => setSocketStatus('DOWN'));
    const refreshDuel = () => void queryClient.invalidateQueries({ queryKey: ['bramx-compare'] });
    const refreshFixture = () => void queryClient.invalidateQueries({ queryKey: ['match-centre'] });
    const refreshFpl = () => {
      for (const rootKey of FPL_UPDATE_INVALIDATION_ROOTS) void queryClient.invalidateQueries({ queryKey: [rootKey] });
    };
    socket.on('bramx:update', refreshDuel);
    socket.on('fixture:update', refreshFixture);
    socket.on('fixture:event', refreshFixture);
    socket.on('fpl:update', refreshFpl);
    return () => {
      socket.io.engine?.off('upgrade', updateTransport);
      socket.off('bramx:update', refreshDuel);
      socket.off('fixture:update', refreshFixture);
      socket.off('fixture:event', refreshFixture);
      socket.off('fpl:update', refreshFpl);
      socket.close();
    };
  }, [queryClient]);

  const teamMap = useMemo(() => new Map(bootstrapQuery.data?.teams.map((team) => [team.id, team.short_name]) ?? []), [bootstrapQuery.data]);
  const providerFixtures = useMemo(() => {
    const map = new Map<string, ProviderFixtureBundle>();
    for (const row of matchCentreQuery.data?.schedule ?? []) map.set(row.fixture.id, row);
    for (const row of matchCentreQuery.data?.live ?? []) map.set(row.fixture.id, row);
    return [...map.values()];
  }, [matchCentreQuery.data]);
  const providerGroups = useMemo(() => groupProviderFixtures(providerFixtures), [providerFixtures]);
  const fplGroups = useMemo(() => groupFplFixtures(fplFixturesQuery.data ?? []), [fplFixturesQuery.data]);
  const targetGwProviderFixtures = useMemo(() => {
    const ids = new Set((fplFixturesQuery.data ?? []).map((fixture) => `fpl:${fixture.id}`));
    return providerFixtures.filter((row) => ids.has(row.fixture.id));
  }, [providerFixtures, fplFixturesQuery.data]);
  const liveMatchByTeam = useMemo(() => mode === 'LIVE' ? buildTeamMatchDisplayStates(targetGwProviderFixtures) : new Map(), [mode, targetGwProviderFixtures]);
  const espnApplied = useMemo(() => targetGwProviderFixtures.some((row) => row.fixture.sourceProvider.toLowerCase().includes('espn-live-edge')), [targetGwProviderFixtures]);
  const useProviderCentre = mode === 'LIVE' && providerFixtures.length > 0;
  const selectedProviderFixture = selectedFixtureId?.startsWith('provider:') ? providerFixtures.find((row) => `provider:${row.fixture.id}` === selectedFixtureId) ?? null : null;
  const selectedFplFixture = selectedFixtureId?.startsWith('fpl:') ? (fplFixturesQuery.data ?? []).find((row) => `fpl:${row.id}` === selectedFixtureId) ?? null : null;
  const selectedSimulationFixtureId = selectedProviderFixture?.fixture.id ?? (selectedFplFixture ? `fpl:${selectedFplFixture.id}` : null);
  const fplHealth = healthQuery.data?.feeds.find((feed) => feed.name === 'FPL Public JSON') ?? null;

  const duel = compareQuery.data;
  const radarData = radarMode === 'OVERALL' ? overallLeagueQuery.data : miniLeagueQuery.data;
  const radarError = radarMode === 'OVERALL' ? overallLeagueQuery.error : miniLeagueQuery.error;
  const bramMiniLeagueRow = mode === 'REPLAY' ? null : miniLeagueQuery.data?.entries.find((entry) => String(entry.entry) === (activeDuel?.entryA ?? store.entryA)) ?? null;
  const duelComplete = isDuelComplete(duel ?? null);
  const finalResult = duelComplete && duel ? finalDuelResult(duel) : null;

  function selectGameweek(next: number) {
    setSelectedGameweek(next);
    const nextMode = resolveSelectedGameweekContext(gameweekQuery.data, next).mode;
    setControlMessage(`GW${next} · ${nextMode ?? 'LOADING'} context selected.`);
  }

  async function runDuel(entryA = store.entryA, entryB = store.entryB, source = 'DUEL') {
    if (mode === 'REPLAY' || !apiScope) {
      setControlMessage(`GW${targetGw ?? '—'} REPLAY · current projection/simulation path is disabled pending historical-data audit.`);
      return;
    }
    if (!/^\d+$/.test(entryA) || !/^\d+$/.test(entryB)) {
      setControlMessage('ERROR · vul twee geldige numerieke FPL entry IDs in.');
      return;
    }
    if (typeof targetGw !== 'number') {
      setControlMessage('WAIT · target gameweek is nog niet geladen.');
      return;
    }
    if (duelRunning) return;

    setDuelRunning(true);
    setControlMessage(`${source} · server-berekening gestart…`);
    setActiveDuel({ entryA, entryB });
    try {
      const result = await queryClient.fetchQuery({
        queryKey: ['bramx-compare', entryA, entryB, targetGw, apiScope, plannedForGw],
        queryFn: () => compareEntries(entryA, entryB, targetGw, apiScope, plannedForGw),
        staleTime: 0,
      });
      const stamp = new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      if (isDuelComplete(result)) {
        setControlMessage(`GW${result.gameweek} FINAL ${stamp} · ${result.entryA.teamName} ${result.entryA.provisionalPointsFromPublishedPicks}–${result.entryB.provisionalPointsFromPublishedPicks} ${result.entryB.teamName} · deterministic`);
      } else {
        setControlMessage(`${result.scope === 'NEXT_PREVIEW' ? `GW${result.gameweek} PREVIEW` : `GW${result.gameweek} LIVE`} ${stamp} · ${result.entryA.teamName} vs ${result.entryB.teamName}`);
      }
    } catch (error) {
      setControlMessage(`SIM ERROR · ${error instanceof Error ? error.message : 'onbekende fout'}`);
    } finally {
      setDuelRunning(false);
    }
  }

  async function runPlannedDraft(plan: PlannedSquadPayload): Promise<void> {
    if (!activeDuel || !apiScope || typeof targetGw !== 'number') throw new Error('PLANNED_RUN_CONTEXT_MISSING');
    if (plan.gameweek !== targetGw) throw new Error('PLANNED_RUN_GW_MISMATCH');
    if (!adminUnlocked) throw new Error('ADMIN_AUTH_REQUIRED');
    if (duelRunning) throw new Error('RUN_ALREADY_IN_PROGRESS');

    setDuelRunning(true);
    setAnalysisPlan(plan);
    setControlMessage(`PLANNED GW${plan.gameweek} · 5K server-run gestart…`);
    const key = ['bramx-compare', activeDuel.entryA, activeDuel.entryB, targetGw, apiScope, plan] as const;
    try {
      const result = await queryClient.fetchQuery({
        queryKey: key,
        queryFn: () => compareEntries(activeDuel.entryA, activeDuel.entryB, targetGw, apiScope, plan),
        staleTime: 0,
      });
      const stamp = new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setControlMessage(`PLANNED GW${result.gameweek} · RUN 5K COMPLETE ${stamp} · serverresultaat geladen.`);
    } catch (error) {
      setControlMessage(`PLANNED RUN ERROR · ${error instanceof Error ? error.message : 'onbekende fout'}`);
      throw error;
    } finally {
      setDuelRunning(false);
    }
  }

  function applyDuel(event?: FormEvent) {
    event?.preventDefault();
    void runDuel();
  }

  async function loadMiniLeague() {
    if (mode === 'REPLAY') return;
    const parsed = parseLeagueId(store.leagueId);
    if (!parsed) {
      setControlMessage('ERROR · vul een geldige classic mini-league ID of FPL league URL in.');
      return;
    }
    if (parsed === overallLeagueId) {
      const fallback = configQuery.data?.defaultLeagueId ?? 479308;
      store.setLeagueId(String(fallback));
      setControlMessage(`OVERALL #${overallLeagueId} is geen mini-league veld · gebruik OVERALL TOP 100. Mini hersteld naar ${fallback}.`);
      return;
    }
    if (radarLoading) return;
    setRadarLoading('MINI');
    setActiveMiniLeagueId(parsed);
    setRadarMode('MINI');
    try {
      const result = await queryClient.fetchQuery({ queryKey: ['mini-league-top100', parsed], queryFn: () => getLeagueStandings(parsed, 100), staleTime: 0 });
      setControlMessage(`MINI-LEAGUE · ${result.league.name} · ${result.entries.length} entries geladen`);
      scrollToRadar();
    } catch (error) {
      setControlMessage(`MINI-LEAGUE ERROR · ${error instanceof Error ? error.message : 'onbekende fout'}`);
    } finally {
      setRadarLoading(null);
    }
  }

  async function loadOverallTop100() {
    if (mode === 'REPLAY' || radarLoading) return;
    setRadarLoading('OVERALL');
    setRadarMode('OVERALL');
    try {
      const result = await queryClient.fetchQuery({ queryKey: ['overall-top100', overallLeagueId], queryFn: () => getLeagueStandings(overallLeagueId, 100), staleTime: 0 });
      setControlMessage(`OVERALL TOP 100 LOADED · ${result.entries.length} managers · klik DUEL om te vergelijken`);
      scrollToRadar();
    } catch (error) {
      setControlMessage(`OVERALL TOP 100 ERROR · ${error instanceof Error ? error.message : 'onbekende fout'}`);
    } finally {
      setRadarLoading(null);
    }
  }

  function scrollToRadar() {
    window.setTimeout(() => leagueBoardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function selectLeagueEntry(entry: LeagueStanding) {
    if (mode === 'REPLAY') return;
    const rival = String(entry.entry);
    store.setEntryB(rival);
    store.setNicknameB(entry.entry_name || entry.player_name || 'Rival');
    const bram = /^\d+$/.test(store.entryA) ? store.entryA : activeDuel?.entryA;
    if (bram) void runDuel(bram, rival, `${radarMode} #${entry.rank}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function selectViktor() {
    if (mode === 'REPLAY') return;
    const viktor = configQuery.data?.defaultEntries.viktor;
    const bram = configQuery.data?.defaultEntries.bram;
    if (!viktor) {
      setControlMessage('VIKTOR · default entry ID ontbreekt in public config.');
      return;
    }
    const rival = String(viktor);
    store.setEntryB(rival);
    store.setNicknameB('Viktor');
    const a = store.entryA || (bram ? String(bram) : '');
    if (/^\d+$/.test(a)) void runDuel(a, rival, 'VIKTOR');
  }

  function resetDuelDefaults() {
    if (mode === 'REPLAY') return;
    const bram = configQuery.data?.defaultEntries.bram;
    const viktor = configQuery.data?.defaultEntries.viktor;
    if (!bram || !viktor) {
      setControlMessage('DEFAULT DUEL ERROR · Bram/Viktor entry IDs ontbreken in public config.');
      return;
    }
    store.setEntryA(String(bram));
    store.setNicknameA('Bram');
    store.setEntryB(String(viktor));
    store.setNicknameB('Viktor');
    void runDuel(String(bram), String(viktor), 'RESET BRAM + VIKTOR');
  }

  const previousGw = targetGw ? adjacentGameweek(targetGw, -1) : null;
  const nextGw = targetGw ? adjacentGameweek(targetGw, 1) : null;

  return (
    <div className="min-h-screen bg-[#04090e] text-slate-200">
      <header className="topbar">
        <div className="mx-auto flex max-w-[1880px] items-center justify-between gap-4 px-4 py-4 xl:px-6">
          <div className="flex items-center gap-3">
            <div className="brand-mark">BX</div>
            <div>
              <div className="flex items-center gap-2"><h1 className="text-lg font-black tracking-[0.08em] text-white">{productName}</h1><span className="pro-chip">PRO LIVE</span></div>
              <p className="text-[9px] uppercase tracking-[0.22em] text-slate-600">{productSubtitle}</p>
            </div>
          </div>
          <div className="topbar-data-stack">
            {mode === 'REPLAY' && <span className="system-feed-scope">CURRENT SYSTEM FEEDS</span>}
            <ConnectionPills health={healthQuery.data} realtimeStatus={socketStatus} realtimeName={socketTransport === 'websocket' ? 'WebSocket' : 'Realtime'} espnApplied={espnApplied} />
            <DataHealthControl health={healthQuery.data} realtimeStatus={socketStatus} realtimeName={socketTransport === 'websocket' ? 'WebSocket' : 'Realtime'} sources={duel?.dataSources ?? []} />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1880px] p-3 md:p-5 xl:p-6">
        <section className="zone-shell">
          <ZoneHeading number="01" title="GAMEWEEK / MODE" subtitle="One selected Gameweek Context. Mode is derived, never independently controlled." />
          <div className="panel gameweek-context-panel">
            <div className="gameweek-navigator">
              <button type="button" className="gw-nav-side" disabled={previousGw === null} onClick={() => previousGw && selectGameweek(previousGw)}>‹ {previousGw ? `GW${previousGw}` : 'START'}</button>
              <div className="gw-nav-current"><span>SELECTED GAMEWEEK</span><strong>GW{targetGw ?? '—'} · {mode ?? 'LOADING'}</strong><small>{modeCopy(mode)}</small></div>
              <button type="button" className="gw-nav-side" disabled={nextGw === null} onClick={() => nextGw && selectGameweek(nextGw)}>{nextGw ? `GW${nextGw}` : 'END'} ›</button>
            </div>
            <div className="command-strip mt-3">
              <div className="command-stat"><span>CURRENT TRUTH</span><strong>GW{gw ?? '—'} · {gameweekQuery.data?.stateSource ?? '—'}</strong></div>
              <div className="command-stat deadline-stat"><span>NEXT DEADLINE</span><strong>{deadlineCountdown(gameweekQuery.data?.nextEvent?.deadline_time ?? null, clockMs)}</strong></div>
              <div className="command-stat"><span>SELECTED</span><strong>GW{targetGw ?? '—'} · {mode ?? '—'}</strong></div>
              <div className="command-stat"><span>RULE</span><strong>{mode === 'REPLAY' ? 'STORED HISTORY ONLY' : mode === 'PREVIEW' ? 'DECISION PATH' : 'OBSERVE NOW'}</strong></div>
            </div>
            {mode === 'REPLAY' && <div className="context-warning">REPLAY SAFE MODE · official historical FPL fixtures/results are available. BRAM-X duel/decision reconstruction remains disabled until production snapshots are proven.</div>}
          </div>
        </section>

        <section className="zone-shell mt-3">
          <ZoneHeading number="02" title="MATCH CENTRE" subtitle={mode === 'LIVE' ? 'Current time-window with FPL truth + safe ESPN live edge.' : `Official FPL fixtures for selected GW${targetGw ?? '—'}.`} />
          <div className="panel">
            <div className="match-groups">
              {(useProviderCentre ? (['PREVIOUS', 'LIVE', 'NEXT'] as MatchGroup[]).map((group) => (
                <ProviderMatchGroup key={group} group={group} fixtures={providerGroups[group]} selectedId={selectedFixtureId} onSelect={setSelectedFixtureId} />
              )) : (['PREVIOUS', 'LIVE', 'NEXT'] as MatchGroup[]).map((group) => (
                <FplMatchGroup key={group} group={group} fixtures={fplGroups[group]} teamMap={teamMap} selectedId={selectedFixtureId} onSelect={setSelectedFixtureId} />
              )))}
            </div>
            {selectedProviderFixture && <ProviderMatchDetail fixture={selectedProviderFixture} onClose={() => setSelectedFixtureId(null)} />}
            {selectedFplFixture && <FplMatchDetail fixture={selectedFplFixture} teamMap={teamMap} fplHealth={fplHealth} mode={mode} onClose={() => setSelectedFixtureId(null)} />}
            {!selectedFixtureId && <p className="match-detail-hint">Selecteer een fixture voor kickoff, source en freshness. Geen events/stats worden in v0.3.7.1 productised.</p>}
          </div>
          {mode === 'LIVE' && matchCentreQuery.error && <ErrorBox title="Match centre" error={matchCentreQuery.error} />}
          {fplFixturesQuery.error && <ErrorBox title="FPL fixtures" error={fplFixturesQuery.error} />}
        </section>

        <section className="zone-shell mt-3">
          <ZoneHeading number="03" title="MY FPL / DUEL" subtitle={mode === 'REPLAY' ? 'Historical duel intelligence gated pending audit.' : 'Compact score state; player rows stay hidden until requested.'} />
          {mode === 'REPLAY' ? (
            <div className="panel replay-gate"><div className="eyebrow">GW{targetGw ?? '—'} · REPLAY SAFE MODE</div><h2>OFFICIAL RESULTS READY · BRAM-X REPLAY GATED</h2><p>Historical FPL fixtures/results above are real. Current compare/projection/optimizer paths are blocked; stored SimulationRun/PredictionFreeze/snapshots must be proven before BRAM-X historical intelligence is shown.</p></div>
          ) : (
            <>
              <CompactDuel duel={duel ?? null} loading={compareQuery.isLoading || duelRunning} onViewPlayers={() => setPlayersOpen((open) => !open)} playersOpen={playersOpen} />
              <details className="progressive panel mt-3">
                <summary>DUEL SETTINGS</summary>
                <div className="mt-4 grid gap-3 xl:grid-cols-[1.05fr_1.05fr_1.15fr_auto] xl:items-end">
                  <EntryInput label="BRAM FPL ENTRY" nickname={store.nicknameA} entry={store.entryA} onNickname={store.setNicknameA} onEntry={store.setEntryA} />
                  <EntryInput label="RIVAL FPL ENTRY" nickname={store.nicknameB} entry={store.entryB} onNickname={store.setNicknameB} onEntry={store.setEntryB} />
                  <label className="block"><span className="input-label">MINI-LEAGUE</span><div className="flex gap-2"><input className="input" value={store.leagueId} onChange={(e) => store.setLeagueId(e.target.value)} placeholder="classic league ID / URL" /><button type="button" className={radarMode === 'MINI' ? 'btn-secondary active' : 'btn-secondary'} onClick={() => void loadMiniLeague()} disabled={radarLoading !== null}>{radarLoading === 'MINI' ? 'LOADING…' : 'LOAD MINI'}</button></div></label>
                  <div className="flex flex-wrap justify-end gap-2"><button type="button" className={radarMode === 'OVERALL' ? 'btn-secondary active' : 'btn-secondary'} onClick={() => void loadOverallTop100()} disabled={radarLoading !== null}>{radarLoading === 'OVERALL' ? 'LOADING…' : 'OVERALL TOP 100'}</button><button type="button" className="btn-secondary" onClick={resetDuelDefaults} disabled={duelRunning}>RESET</button><button type="button" className="btn-secondary" onClick={selectViktor} disabled={duelRunning}>VIKTOR</button><button type="button" className="btn-primary" onClick={() => applyDuel()} disabled={duelRunning}>{duelRunning ? 'RUNNING…' : 'REFRESH DUEL'}</button></div>
                  <div className="xl:col-span-4 control-message">{controlMessage}</div>
                </div>
              </details>
              {compareQuery.error && <ErrorBox title="Duel" error={compareQuery.error} />}
              {playersOpen && duel && <div className="mt-3 grid gap-3 2xl:grid-cols-2"><SquadPanel side="A" nickname={store.nicknameA} entry={duel.entryA} players={duel.players} simulation={duel.simulation} preview={duel.scope === 'NEXT_PREVIEW'} picksBasisGameweek={duel.picksBasisGameweek} basis={duel.entryABasis} liveMatchByTeam={liveMatchByTeam} /><SquadPanel side="B" nickname={store.nicknameB} entry={duel.entryB} players={duel.players} simulation={duel.simulation} preview={duel.scope === 'NEXT_PREVIEW'} picksBasisGameweek={duel.picksBasisGameweek} basis={duel.entryBBasis} liveMatchByTeam={liveMatchByTeam} /></div>}
            </>
          )}
        </section>

        <section className="zone-shell mt-3">
          <ZoneHeading number="04" title="DECISION INTELLIGENCE" subtitle={mode === 'PREVIEW' ? 'Plan and decide.' : mode === 'LIVE' ? 'Observe model movement without letting future transfer tooling dominate.' : 'Historical intelligence remains gated until stored inputs are verified.'} />
          {mode === 'PREVIEW' && <div className="space-y-3"><DecisionLab duel={duel ?? null} entryA={activeDuel?.entryA ?? store.entryA} targetGameweek={targetGw} elements={bootstrapQuery.data?.elements ?? []} planned={store.plannedA} simulatedPlan={plannedForGw} onChange={store.setPlannedA} onRun={runPlannedDraft} onRefreshPublicData={async () => { await bootstrapQuery.refetch(); }} /><DecisionIntelligence entryA={activeDuel?.entryA ?? store.entryA} entryB={activeDuel?.entryB ?? store.entryB} targetGameweek={targetGw} planned={store.plannedA} adminUnlocked={adminUnlocked} elements={bootstrapQuery.data?.elements ?? []} teams={bootstrapQuery.data?.teams ?? []} onApplyPlan={(plan) => { store.setPlannedA(plan); setControlMessage(`ROUTE LOADED · ${plan.routeLabel ?? 'route'} · review draft, then explicit RUN 5K.`); }} /></div>}
          {mode === 'LIVE' && <div className="space-y-3"><IntelPanel duel={duel ?? null} selectedFixture={selectedProviderFixture} adminUnlocked={adminUnlocked} entryA={activeDuel?.entryA ?? store.entryA} gameweek={targetGw} /><details className="progressive panel"><summary>SIMULATION / REPRODUCIBILITY DETAIL</summary><div className="mt-3"><SimulationPanel duel={duel ?? null} selectedFixtureId={selectedSimulationFixtureId} /></div></details></div>}
          {mode === 'REPLAY' && <div className="panel replay-gate"><div className="eyebrow">REPLAY DATA AUDIT REQUIRED</div><h2>NO HISTORICAL DECISION RECONSTRUCTION</h2><p>Phase A intentionally stops here. Stored SimulationRun/PredictionFreeze/snapshot coverage must be verified read-only before any historical intelligence viewer is enabled.</p></div>}
          {mode !== 'REPLAY' && <details className="progressive panel mt-3"><summary>LEAGUE RADAR</summary><div className="mt-4" ref={leagueBoardRef}><LeagueBoard scope={radarMode} leagueName={radarData?.league.name ?? (radarMode === 'OVERALL' ? 'Overall' : '')} entries={radarData?.entries ?? []} bramEntryId={activeDuel ? Number(activeDuel.entryA) : null} rivalEntryId={activeDuel ? Number(activeDuel.entryB) : null} bramReferenceTotal={duel?.entryA.overallPoints ?? null} onSelect={selectLeagueEntry} /></div>{radarError && <ErrorBox title={radarMode === 'OVERALL' ? 'Overall Top 100' : 'Mini-league'} error={radarError} />}</details>}
        </section>
      </main>
    </div>
  );
}

function ZoneHeading({ number, title, subtitle }: { number: string; title: string; subtitle: string }) {
  return <div className="zone-heading"><span>{number}</span><div><h2>{title}</h2><p>{subtitle}</p></div></div>;
}

function ConnectionPills({ health, realtimeStatus, realtimeName, espnApplied }: { health: FeedHealthResponse | undefined; realtimeStatus: string; realtimeName: string; espnApplied: boolean }) {
  const pills = connectionPills(health, realtimeStatus, realtimeName, espnApplied);
  return <div className="connection-pills" aria-label="BRAM-X source connections">{pills.map((pill) => <div key={pill.key} className={`connection-pill source-${pill.tone}`} title={pill.detail}><span className="connection-dot" /><strong>{pill.label}</strong><i>{pill.status}</i></div>)}</div>;
}

function DataHealthControl({ health, realtimeStatus, realtimeName, sources }: { health: FeedHealthResponse | undefined; realtimeStatus: string; realtimeName: string; sources: Array<{ name: string; status: string; detail: string }> }) {
  const status = deriveDataHealthStatus(health);
  const grouped = groupFeeds(health);
  return (
    <details className={`data-health-control data-${status.toLowerCase()}`}>
      <summary><span className="data-dot" /> DATA · {status}</summary>
      <div className="data-health-popover">
        <div className="data-health-truthline"><strong>LIVE FOUNDATION</strong><span>FPL = official FPL/player truth · ESPN = match score/minute/status edge only · WS = transport · Vaastav = prior support</span></div><div className="data-health-footnote">FPL PLAYER LAYER · official points/minutes · price/ownership/form/transfers · xG/xA/xGI/xGC · BPS/bonus · starts · tackles/CBI/recoveries/defensive contributions. Current BRAM-X model uses only explicitly wired fields; availability does not imply causal use.</div>
        {grouped.map(({ group, feeds }) => feeds.length > 0 && <div key={group} className="data-health-group">
          <div className="data-health-section-title">{group}</div>
          {feeds.map((feed) => <div key={feed.name} className="data-feed-row"><div><strong>{feed.name}</strong><small>{feed.message ?? 'No detail'}</small></div><span className={`feed-status source-${feedTone(feed.status)}`}>{feed.status}{feed.freshnessSeconds === null ? '' : ` · ${feed.freshnessSeconds}s`}</span></div>)}
        </div>)}
        <div className="data-health-group">
          <div className="data-health-section-title">TRANSPORT</div>
          <div className="data-feed-row"><div><strong>{realtimeName}</strong><small>Realtime UI invalidation/transport. This is not a football truth source.</small></div><span className={`feed-status source-${feedTone(realtimeStatus)}`}>{realtimeStatus}</span></div>
        </div>
        <div className="data-health-section-title data-health-section-spaced">ACTIVE DUEL SOURCE CONTRACT</div>
        {sources.length > 0 ? sources.map((source) => <div key={`${source.name}-${source.status}`} className="data-feed-row"><div><strong>{source.name}</strong><small>{source.detail}</small></div><span>{source.status}</span></div>) : <div className="data-health-empty">Duel-specific source arbitration loads with the active LIVE/PREVIEW duel.</div>}
        <div className="data-health-footnote">FPL Public JSON is authoritative for FPL state. ESPN can only overlay score/minute/status after safe fixture matching. Priors, learning sources and test providers never become live truth automatically.</div>
      </div>
    </details>
  );
}

function CompactDuel({ duel, loading, onViewPlayers, playersOpen }: { duel: BramxCompareResponse | null; loading: boolean; onViewPlayers(): void; playersOpen: boolean }) {
  if (!duel) return <div className="panel"><div className="eyebrow">LIVE DUEL</div><h2 className="mt-1 text-lg font-black text-white">{loading ? 'Loading duel…' : 'Duel not loaded'}</h2><p className="mt-2 text-xs text-slate-600">Official/live points, remaining projection, projected total and win probability appear here.</p></div>;
  const a = duel.entryA;
  const b = duel.entryB;
  const summary = compactDuelSummary(duel);
  const aWin = summary.bram.winProbability;
  const bWin = summary.rival.winProbability;
  return (
    <div className="panel compact-duel">
      <div className="flex flex-wrap items-start justify-between gap-3"><div><div className="eyebrow">{duel.scope === 'NEXT_PREVIEW' ? `GW${duel.gameweek} · PREVIEW DUEL` : `GW${duel.gameweek} · LIVE DUEL`}</div><h2 className="mt-1 text-lg font-black text-white">{a.teamName} <span className="text-slate-600">vs</span> {b.teamName}</h2></div><button type="button" className="btn-secondary" onClick={onViewPlayers}>{playersOpen ? 'HIDE PLAYERS' : 'VIEW PLAYERS'}</button></div>
      <div className="duel-metrics"><DuelMetric label="LIVE POINTS" a={summary.bram.livePoints} b={summary.rival.livePoints} /><DuelMetric label="REMAINING" a={summary.bram.remaining} b={summary.rival.remaining} decimals /><DuelMetric label="PROJECTED" a={summary.bram.projected} b={summary.rival.projected} decimals /><DuelMetric label="GW WIN %" a={aWin} b={bWin} suffix="%" decimals /></div>
      <div className="duel-probability"><span style={{ width: `${Math.max(0, Math.min(100, aWin))}%` }} /></div>
      <div className="duel-probability-label"><span>{a.teamName} {aWin.toFixed(1)}%</span><span>draw {summary.drawProbability.toFixed(1)}%</span><span>{bWin.toFixed(1)}% {b.teamName}</span></div>
      <p className="mt-3 text-[10px] text-slate-600">C/VC, autosub, hit and official-points correctness remain in the existing calculation contract; only presentation is compacted.</p>
    </div>
  );
}

function DuelMetric({ label, a, b, decimals = false, suffix = '' }: { label: string; a: number; b: number; decimals?: boolean; suffix?: string }) {
  const fmt = (value: number) => `${decimals ? value.toFixed(1) : Math.round(value)}${suffix}`;
  return <div className="duel-metric"><span>{label}</span><div><strong>{fmt(a)}</strong><i>BRAM</i></div><div><strong>{fmt(b)}</strong><i>RIVAL</i></div></div>;
}

function ProviderMatchGroup({ group, fixtures, selectedId, onSelect }: { group: MatchGroup; fixtures: ProviderFixtureBundle[]; selectedId: string | null; onSelect(id: string): void }) {
  return <MatchGroupShell group={group} empty={fixtures.length === 0}>{fixtures.map((bundle) => { const home = bundle.participants.find((p) => p.location === 'home') ?? bundle.participants[0]; const away = bundle.participants.find((p) => p.location === 'away') ?? bundle.participants[1]; const id = `provider:${bundle.fixture.id}`; return <button key={id} type="button" onClick={() => onSelect(id)} className={`match-card ${group === 'LIVE' ? 'live' : ''} ${selectedId === id ? 'active' : ''}`}><div className="fixture-status">{group === 'LIVE' && bundle.fixture.minute !== null ? `${bundle.fixture.minute}' · ${bundle.fixture.status}` : bundle.fixture.status === 'STALE' ? 'ENDED · SOURCE STALE' : bundle.fixture.status}</div><div className="fixture-team"><span>{shortTeam(home?.name)}</span><strong>{bundle.fixture.homeScore ?? '—'}</strong></div><div className="fixture-team"><span>{shortTeam(away?.name)}</span><strong>{bundle.fixture.awayScore ?? '—'}</strong></div><div className="fixture-time">{formatProviderKickoff(bundle.startingAt)}</div></button>; })}</MatchGroupShell>;
}

function FplMatchGroup({ group, fixtures, teamMap, selectedId, onSelect }: { group: MatchGroup; fixtures: Fixture[]; teamMap: Map<number, string>; selectedId: string | null; onSelect(id: string): void }) {
  return <MatchGroupShell group={group} empty={fixtures.length === 0}>{fixtures.map((fixture) => { const id = `fpl:${fixture.id}`; return <button key={id} type="button" onClick={() => onSelect(id)} className={`match-card ${group === 'LIVE' ? 'live' : ''} ${selectedId === id ? 'active' : ''}`}><div className="fixture-status">FPL · {fixture.finished ? 'FT' : fixture.started ? `LIVE ${fixture.minutes ?? ''}'` : kickoff(fixture.kickoff_time)}</div><div className="fixture-team"><span>{teamMap.get(fixture.team_h) ?? fixture.team_h}</span><strong>{fixture.team_h_score ?? '—'}</strong></div><div className="fixture-team"><span>{teamMap.get(fixture.team_a) ?? fixture.team_a}</span><strong>{fixture.team_a_score ?? '—'}</strong></div></button>; })}</MatchGroupShell>;
}

function MatchGroupShell({ group, empty, children }: { group: MatchGroup; empty: boolean; children: ReactNode }) {
  return <div className={`match-group match-group-${group.toLowerCase()}`}><div className="match-group-title"><span>{group}</span>{group === 'LIVE' && <i>NOW</i>}</div><div className="match-group-list">{empty ? <div className="match-empty">No fixtures</div> : children}</div></div>;
}

function ProviderMatchDetail({ fixture, onClose }: { fixture: ProviderFixtureBundle; onClose(): void }) {
  const home = fixture.participants.find((participant) => participant.location === 'home') ?? fixture.participants[0];
  const away = fixture.participants.find((participant) => participant.location === 'away') ?? fixture.participants[1];
  const freshness = fixture.freshness.score;
  const provenance = providerProvenance(fixture.fixture.sourceProvider);
  return <div className="match-detail"><div className="match-detail-main"><div className="eyebrow">MATCH DETAIL · LIVE PROVENANCE</div><h3>{home?.name ?? 'Home'} {fixture.fixture.homeScore ?? '—'}–{fixture.fixture.awayScore ?? '—'} {away?.name ?? 'Away'}</h3><p>{fixture.fixture.minute !== null ? `${fixture.fixture.minute}' · ` : ''}{fixture.fixture.status} · kickoff {formatProviderKickoff(fixture.startingAt)}</p><div className="provenance-grid"><span>OFFICIAL</span><strong>{provenance.official}</strong><span>LIVE EDGE</span><strong>{provenance.liveEdge}</strong><span>RESOLVED</span><strong>{provenance.resolved}</strong><span>FALLBACK</span><strong>{provenance.fallback}</strong><span>FRESHNESS</span><strong>{freshness?.status ?? 'UNKNOWN'}{freshness?.ageSeconds == null ? '' : ` · ${freshness.ageSeconds}s`}</strong></div><p className="source-provider-line">runtime sourceProvider: {fixture.fixture.sourceProvider}</p></div><button type="button" className="btn-micro" onClick={onClose}>CLOSE</button></div>;
}

function FplMatchDetail({ fixture, teamMap, fplHealth, mode, onClose }: { fixture: Fixture; teamMap: Map<number, string>; fplHealth: FeedHealthResponse['feeds'][number] | null; mode: GameweekMode | null; onClose(): void }) {
  const fixtureState = fixture.finished ? 'FT' : fixture.started ? `LIVE ${fixture.minutes ?? ''}'` : 'SCHEDULED';
  const contextState = mode === 'REPLAY' ? 'HISTORICAL' : mode === 'PREVIEW' ? 'PREVIEW' : 'CURRENT';
  return <div className="match-detail"><div><div className="eyebrow">MATCH DETAIL · OFFICIAL FPL</div><h3>{teamMap.get(fixture.team_h) ?? fixture.team_h} {fixture.team_h_score ?? '—'}–{fixture.team_a_score ?? '—'} {teamMap.get(fixture.team_a) ?? fixture.team_a}</h3><p>{fixtureState} · kickoff {kickoff(fixture.kickoff_time)}</p><div className="provenance-grid compact-provenance"><span>FIXTURE CONTEXT</span><strong>{contextState} · {fixtureState}</strong><span>OFFICIAL SOURCE</span><strong>FPL Public JSON</strong><span>SOURCE HEALTH NOW</span><strong>{fplHealth?.status ?? 'UNKNOWN'}{fplHealth?.freshnessSeconds == null ? '' : ` · ${fplHealth.freshnessSeconds}s`}</strong></div>{mode === 'REPLAY' && <p className="source-provider-line">Source health describes the current FPL connection, not the historical state at that past kickoff.</p>}</div><button type="button" className="btn-micro" onClick={onClose}>CLOSE</button></div>;
}

function modeCopy(mode: GameweekMode | null): string {
  if (mode === 'LIVE') return 'Observe what is happening now.';
  if (mode === 'PREVIEW') return 'Decide for a future gameweek.';
  if (mode === 'REPLAY') return 'Learn only from provably stored historical data.';
  return 'Resolving current gameweek…';
}

function SimulationPanel({ duel, selectedFixtureId }: { duel: BramxCompareResponse | null; selectedFixtureId: string | null }) {
  const [replayStatus, setReplayStatus] = useState<'IDLE' | 'RUNNING' | 'VERIFIED' | 'FAILED'>('IDLE');
  if (!duel) {
    return <section className="panel"><div className="eyebrow">BRAM-X SIM</div><p className="mt-2 text-sm text-slate-600">Duel laden om GAMEWEEK POINTS MC + FIXTURE SWING MC te starten.</p></section>;
  }
  const loadedDuel = duel;
  const complete = isDuelComplete(loadedDuel);
  const a = loadedDuel.simulation.gameweek.a;
  const b = loadedDuel.simulation.gameweek.b;
  const selectedFplFixtureId = selectedFixtureId?.startsWith('fpl:') ? Number(selectedFixtureId.slice(4)) : Number(selectedFixtureId);
  const selectedMatch = (Number.isFinite(selectedFplFixtureId) ? loadedDuel.simulation.matches.find((match) => match.fixtureId === selectedFplFixtureId) : null)
    ?? loadedDuel.simulation.matches.find((match) => loadedDuel.players.some((player) => player.fixtureProjections.some((fixture) => fixture.fixtureId === match.fixtureId && fixture.status === 'LIVE')))
    ?? loadedDuel.simulation.matches[0]
    ?? null;
  const selectedMatchLabel = selectedMatch ? fixtureDuelLabel(loadedDuel, selectedMatch.fixtureId) : null;
  async function replayExact() {
    if (!loadedDuel.replay.simulationRunId || replayStatus === 'RUNNING') return;
    setReplayStatus('RUNNING');
    try {
      const replay = await replaySimulation(loadedDuel.replay.simulationRunId);
      const verified = replay.exactReplay && replay.seed === loadedDuel.replay.randomSeed && replay.inputSnapshotHash === loadedDuel.replay.inputSnapshotHash
        && replay.result.gameweek.a.mean === loadedDuel.simulation.gameweek.a.mean
        && replay.result.gameweek.b.mean === loadedDuel.simulation.gameweek.b.mean
        && replay.result.gameweek.a.winProbability === loadedDuel.simulation.gameweek.a.winProbability;
      setReplayStatus(verified ? 'VERIFIED' : 'FAILED');
    } catch {
      setReplayStatus('FAILED');
    }
  }
  return (
    <section className="panel sim-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="eyebrow">{duel.scope === 'NEXT_PREVIEW' ? `BRAM-X GW${duel.gameweek} DEADLINE PREVIEW · ACTUAL MONTE CARLO` : complete ? 'BRAM-X GAMEWEEK RESULT · DETERMINISTIC' : 'BRAM-X GAMEWEEK SIM · ACTUAL MONTE CARLO'}</div>
          <h3 className="mt-1 text-base font-black text-white">{duel.scope === 'NEXT_PREVIEW' ? 'Next-GW duel preview' : complete ? 'Gameweek settled' : 'Full-GW duel settlement'}</h3>
          <p className="mt-1 text-[10px] text-slate-600">{duel.scope === 'NEXT_PREVIEW' ? duel.entryABasis === 'PLANNED' ? `Bram = PLANNED GW${duel.gameweek} · rival = LAST-PUBLISHED SCENARIO · ep_next benchmark + prior/market football state.` : `Bram + rival = LAST-PUBLISHED SCENARIO uit GW${duel.picksBasisGameweek} · niet presenteren als toekomstige echte squad.` : complete ? 'Alle relevante fixtures zijn klaar · Remaining EV = 0 · de sim-distributie is volledig ingestort naar de eindscore.' : 'Finished fixed · live-conditioned · future pre-match · formation-aware autosubs · captain→vice fallback'}</p>
        </div>
        <div className="text-right">
          <span className="badge-sim">{duel.scope === 'NEXT_PREVIEW' ? duel.entryABasis === 'PLANNED' ? `PLANNED GW${duel.gameweek} · SIM` : `GW${duel.gameweek} · LAST-SQUAD SCENARIO` : complete ? 'GW COMPLETE · FINAL' : duel.simulation.displayLabel.toUpperCase()}</span>
          <div className="mt-1 text-[9px] text-slate-700">{duel.simulation.modelVersion}</div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        <SimSide name={duel.entryA.teamName} win={a.winProbability} mean={a.mean} range={`${a.p10}–${a.p90}`} align="left" />
        <div className="text-center"><div className="text-[10px] text-slate-600">DRAW</div><div className="mt-1 text-lg font-black text-slate-400">{duel.simulation.gameweek.drawProbability.toFixed(1)}%</div></div>
        <SimSide name={duel.entryB.teamName} win={b.winProbability} mean={b.mean} range={`${b.p10}–${b.p90}`} align="right" />
      </div>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-900"><div className="h-full bg-cyan-300/80" style={{ width: `${Math.max(0, Math.min(100, a.winProbability))}%` }} /></div>

      <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_1.15fr]">
        <div className="sim-scope-card">
          <div className="section-title">FIXTURE SWING MC</div>
          {selectedMatch ? <>
            <div className="mt-2 flex items-end justify-between gap-3"><div><div className="text-xs font-black text-white">{selectedMatchLabel ?? `Fixture #${selectedMatch.fixtureId}`}</div><div className="mt-1 text-[10px] text-slate-600">Incremental fixture swing · not yet a shared football-world simulator</div></div><div className={`text-xl font-black tabular-nums ${selectedMatch.meanEdgeToA >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{selectedMatch.meanEdgeToA >= 0 ? '+' : ''}{selectedMatch.meanEdgeToA.toFixed(1)}</div></div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center"><MiniMetric label="BRAM EDGE" value={`${selectedMatch.aEdgeProbability.toFixed(1)}%`} /><MiniMetric label="P10–P90" value={`${selectedMatch.p10EdgeToA}–${selectedMatch.p90EdgeToA}`} /><MiniMetric label="RIVAL EDGE" value={`${selectedMatch.bEdgeProbability.toFixed(1)}%`} /></div>
          </> : <p className="mt-2 text-xs text-slate-600">Geen fixture-scope beschikbaar.</p>}
        </div>
        <div className="sim-scope-card">
          <div className="section-title">REPRODUCIBILITY / CALIBRATION</div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]"><MiniMetric label="INPUT" value={duel.simulation.inputQuality} /><MiniMetric label="CALIBRATION" value={duel.simulation.calibration.status} /></div>
          <div className="mt-2 text-[9px] leading-4 text-slate-600">seed <span className="mono-select">{duel.replay.randomSeed.slice(0, 26)}…</span></div>
          <div className="text-[9px] leading-4 text-slate-600">input hash <span className="mono-select">{duel.replay.inputSnapshotHash.slice(0, 26)}…</span></div>
          <div className="mt-2 flex flex-wrap items-center gap-2"><span className="text-[9px] text-slate-700">{duel.replay.exactReplayReady ? 'Simulation run persisted for exact replay.' : 'Simulation executed; DB replay persistence unavailable.'}</span>{duel.replay.exactReplayReady && <button type="button" className="btn-micro" onClick={replayExact} disabled={replayStatus === 'RUNNING'}>{replayStatus === 'RUNNING' ? 'REPLAYING…' : replayStatus === 'VERIFIED' ? 'REPLAY VERIFIED' : replayStatus === 'FAILED' ? 'REPLAY FAILED' : 'REPLAY EXACT'}</button>}</div>
        </div>
      </div>
      <p className="mt-3 text-[10px] leading-5 text-slate-600">{complete ? 'Final result: no probability claim is needed because all relevant uncertainty is resolved.' : duel.simulation.calibration.status === 'CALIBRATED' ? 'CALIBRATED SIM is explicitly configured with a calibration version.' : 'SIM is real Monte Carlo, but the Remaining EV input is still MODEL-EST. No calibrated probability claim is made.'}</p>
    </section>
  );
}

function MiniMetric({ label, value }: { label: string; value: string }) { return <div className="mini-metric"><div>{label}</div><strong>{value}</strong></div>; }

function SimSide({ name, win, mean, range, align }: { name: string; win: number; mean: number; range: string; align: 'left' | 'right' }) {
  return <div className={align === 'right' ? 'text-right' : ''}><div className="truncate text-xs font-bold text-slate-400">{name}</div><div className="mt-1 text-3xl font-black tabular-nums text-white">{win.toFixed(1)}%</div><div className="mt-1 text-[10px] text-slate-600">mean {mean} · P10–P90 {range}</div></div>;
}

function IntelPanel({ duel, selectedFixture, adminUnlocked, entryA, gameweek }: { duel: BramxCompareResponse | null; selectedFixture: ProviderFixtureBundle | null; adminUnlocked: boolean; entryA: string; gameweek: number | null }) {
  const [learningMessage, setLearningMessage] = useState('');
  const [learningBusy, setLearningBusy] = useState(false);
  const waterfall = duel?.waterfall.items ?? [];
  const fallback = duel ? [...duel.onlyA, ...duel.onlyB].sort((a, b) => Math.abs((b.livePoints + b.expectedRemaining) * (b.multiplierA - b.multiplierB)) - Math.abs((a.livePoints + a.expectedRemaining) * (a.multiplierA - a.multiplierB))).slice(0, 8) : [];
  return (
    <section className="panel">
      <div className="eyebrow">LIVE EDGE WATERFALL</div>
      <div className="mt-1 flex items-end justify-between gap-3"><h2 className="text-lg font-black text-white">Why the duel moved</h2>{duel && <div className={`text-xl font-black tabular-nums ${duel.waterfall.netDeltaToA >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{duel.waterfall.netDeltaToA >= 0 ? '+' : ''}{duel.waterfall.netDeltaToA.toFixed(1)}</div>}</div>
      <div className="mt-1 text-[9px] uppercase tracking-wide text-slate-700">{duel?.waterfall.mode === 'DELTA' ? 'Since previous server snapshot' : 'Current baseline contribution'} · positive = Bram edge</div><p className="mt-1 text-[9px] leading-4 text-slate-700">Delta is the exact model-edge movement; the reason code is an observed-state attribution heuristic, never a fabricated event.</p>
      <div className="mt-4 space-y-2">
        {waterfall.map((item) => <div key={`${item.elementId}-${item.reasonCode}`} className="delta-row"><div><div className="font-bold text-slate-300">{item.playerName} <span className="reason-chip">{item.reasonCode}</span></div><div className="text-[10px] text-slate-600">{item.detail}</div></div><div className={`font-black tabular-nums ${item.deltaToA >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{item.deltaToA >= 0 ? '+' : ''}{item.deltaToA.toFixed(1)}</div></div>)}
        {waterfall.length === 0 && fallback.map((player) => {
          const edge = (player.livePoints + player.expectedRemaining) * (player.multiplierA - player.multiplierB);
          return <div key={player.elementId} className="delta-row"><div><div className="font-bold text-slate-300">{player.webName}</div><div className="text-[10px] text-slate-600">{player.fixtureStatus} · REM +{player.expectedRemaining.toFixed(1)} · C{player.projectionConfidence}</div></div><div className={`font-black tabular-nums ${edge >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{edge >= 0 ? '+' : ''}{edge.toFixed(1)}</div></div>;
        })}
        {!duel && <p className="text-xs text-slate-600">Duel-data laden voor de waterfall.</p>}
      </div>

      {duel && <div className="mt-5 border-t border-slate-800 pt-4"><div className="section-title">PREDICTION FREEZE</div><div className="mt-2 flex flex-wrap gap-2">{['T_MINUS_60', 'KICKOFF', 'FULL_TIME'].map((freeze) => { const captured = duel.predictionFreezes.states.some((item) => item.freezeType === freeze); return <span key={freeze} className={captured ? 'freeze-chip captured' : 'freeze-chip'}>{freeze.replace('T_MINUS_60', 'T-60')} {captured ? 'CAPTURED' : 'ARMED'}</span>; })}</div><p className="mt-2 text-[9px] leading-4 text-slate-700">T-60 wordt alleen binnen de configured tolerance vastgezet; kickoff/FT bij de eerste geldige observed state. Dit voorkomt achteraf gefabriceerde snapshots.</p></div>}

      <div className="mt-5 border-t border-slate-800 pt-4">
        <div className="section-title">DATA INTEGRITY</div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]">
          <Integrity label="FPL picks/points" status="OFFICIAL" />
          <Integrity label="Remaining EV" status="MODEL-EST" />
          <Integrity label="Duel probability" status={duel?.simulation.label ?? 'SIM'} />
          <Integrity label="Pitch positions" status={selectedFixture?.lineups.length ? 'TACTICAL · PROVIDER' : 'PENDING'} />
          <Integrity label="Event locations" status={selectedFixture?.events.some((event) => event.x !== null && event.y !== null) ? 'PROVIDER' : 'UNAVAILABLE'} />
          <Integrity label="Tracking" status="NOT CONNECTED" />
        </div>
      </div>

      {duel && <div className="mt-5 border-t border-slate-800 pt-4">
        <div className="section-title">LIVE → LEARNING</div>
        <p className="mt-1 text-[9px] leading-4 text-slate-700">Capture pairs frozen prematch predictions with official FPL actuals. CAPTURE ONLY: this never changes CORE/model weights and never reconstructs a missing historical prediction from current data.</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button type="button" className="btn-secondary" disabled={!adminUnlocked || learningBusy || !/^\d+$/.test(entryA) || !gameweek} onClick={async () => {
            if (!gameweek || !/^\d+$/.test(entryA)) return;
            setLearningBusy(true);
            setLearningMessage('CAPTURING…');
            try {
              const result = await captureLearningOutcomes(entryA, gameweek);
              setLearningMessage(`CAPTURED ${result.created} · SKIPPED ${result.skipped} · NO WEIGHT CHANGE`);
            } catch (error) {
              setLearningMessage(`CAPTURE ERROR · ${error instanceof Error ? error.message : 'unknown error'}`);
            } finally {
              setLearningBusy(false);
            }
          }}>{learningBusy ? 'CAPTURING…' : 'CAPTURE ACTUALS'}</button>
          {!adminUnlocked && <span className="text-[9px] text-slate-700">ADMIN REQUIRED</span>}
          {learningMessage && <span className="text-[9px] font-bold text-slate-500">{learningMessage}</span>}
        </div>
      </div>}

      {duel && <div className="mt-5 border-t border-slate-800 pt-4">
        <div className="section-title">SOURCE ARBITRATION</div>
        <p className="mt-1 text-[9px] leading-4 text-slate-700">FPL blijft authoritative voor FPL-state. Market/prior-bronnen mogen alleen hun eigen causale laag voeden; test-only providers krijgen geen truth-status.</p>
        <div className="mt-2 space-y-2">{duel.dataSources.map((source) => <div key={`${source.name}-${source.status}`} className="rounded-lg border border-slate-800 bg-slate-950/50 p-2"><div className="flex items-center justify-between gap-2"><strong className="text-[10px] text-slate-300">{source.name}</strong><span className="mode-chip">{source.status}</span></div><div className="mt-1 text-[9px] leading-4 text-slate-600">{source.detail}</div></div>)}</div>
      </div>}
    </section>
  );
}

function Integrity({ label, status }: { label: string; status: string }) { return <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-2"><div className="text-slate-600">{label}</div><div className="mt-1 font-black text-slate-300">{status}</div></div>; }

function EntryInput(props: { label: string; nickname: string; entry: string; onNickname(value: string): void; onEntry(value: string): void }) {
  return (
    <label className="block">
      <span className="input-label">{props.label}</span>
      <div className="grid grid-cols-[100px_1fr] gap-2">
        <input value={props.nickname} onChange={(e) => props.onNickname(e.target.value)} className="input" aria-label={`${props.label} nickname`} />
        <input value={props.entry} onChange={(e) => props.onEntry(e.target.value.replace(/\D/g, ''))} className="input" inputMode="numeric" placeholder="FPL entry ID" required aria-label={props.label} />
      </div>
    </label>
  );
}

function ErrorBox({ title, error }: { title: string; error: Error }) { return <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-3 text-xs text-rose-300"><strong>{title}:</strong> {error.message}</div>; }

function isDuelComplete(duel: BramxCompareResponse | null): boolean {
  if (!duel || duel.scope === 'NEXT_PREVIEW') return false;
  if (duel.entryA.expectedRemaining !== 0 || duel.entryB.expectedRemaining !== 0) return false;
  return duel.players.every((player) => player.fixtureStatus === 'FINISHED' || player.fixtureStatus === 'NO_FIXTURE');
}

function finalDuelResult(duel: BramxCompareResponse): string {
  const a = duel.entryA.provisionalPointsFromPublishedPicks;
  const b = duel.entryB.provisionalPointsFromPublishedPicks;
  if (a === b) return `DRAW ${a}–${b}`;
  return a > b ? `WIN ${a}–${b}` : `LOSS ${a}–${b}`;
}

function parseLeagueId(value: string): number | null {
  const match = value.match(/(?:leagues\/)?(\d+)/);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}


function fixtureDuelLabel(duel: BramxCompareResponse, fixtureId: number): string {
  const labels = new Set<string>();
  for (const player of duel.players) {
    const fixture = player.fixtureProjections.find((item) => item.fixtureId === fixtureId);
    if (fixture) labels.add(`${player.teamShortName}–${fixture.opponentShortName}`);
  }
  return [...labels][0] ?? `Fixture #${fixtureId}`;
}

function providerDate(value: string): Date {
  return new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`);
}

function nextFixture(fixtures: ProviderFixtureBundle[]): ProviderFixtureBundle | null {
  const now = Date.now();
  return fixtures.find((fixture) => fixture.startingAt && providerDate(fixture.startingAt).getTime() >= now) ?? null;
}

function formatProviderKickoff(value: string | null): string {
  if (!value) return 'TBD';
  const date = providerDate(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('nl-NL', { timeZone: 'Europe/Amsterdam', weekday: 'short', hour: '2-digit', minute: '2-digit' }).format(date);
}

function kickoff(value: string | null): string {
  if (!value) return 'TBD';
  return new Intl.DateTimeFormat('nl-NL', { timeZone: 'Europe/Amsterdam', weekday: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}


function deadlineCountdown(value: string | null, nowMs: number): string {
  if (!value) return '—';
  const deadline = new Date(value).getTime();
  if (!Number.isFinite(deadline)) return '—';
  const diff = deadline - nowMs;
  if (diff <= 0) return 'PASSED';
  const totalMinutes = Math.floor(diff / 60_000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  return days > 0 ? `${days}d ${hours}h ${minutes}m` : `${hours}h ${minutes}m`;
}

function shortTeam(name: string | undefined): string {
  if (!name) return 'TBD';
  return name.length > 18 ? `${name.slice(0, 17)}…` : name;
}
